import React, { useState, useEffect } from "react";
import Header from "./Header";
import CustomerList from "./CustomerList";
import CustomerModal from "./CustomerModal";
import * as XLSX from "xlsx";

import api from "../api/api";

export default function Dashboard({ setIsLoggedIn }) {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]); // 🔹 Filtered customers
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState(null);
  const [username, setUsername] = useState("");
  const [searchText, setSearchText] = useState(""); // 🔹 Search text

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const user = sessionStorage.getItem("username");
      if (!user) {
        alert("Session expired. Please log in again.");
        setIsLoggedIn(false);
        return;
      }
      setUsername(user);

      const res = await api.get("GetDashboardListAPI", { user });
      const rows = res?.GetDashboardListjs?.Table || [];
      const formatted = rows.map((row, index) => ({
        id: index + 1,
        oid: row.oid,
        Vid: row.vid,
        name: row.vname,
        mobile: row.mobile,
        address: row.Address1,
        circle: row.Division,
        OrdNo: row.oid,
        Item: "",
        ItemCnt: 1,
        ODate: row.orderdate?.split("T")[0],
        DvlDate: row.DeliveryDate?.split("T")[0],
        Amount: row.Amount,
        Paymode: row.paymode,
        UserType: row.UserType,
        verified: row.UserType === "Existing User",
        DvlRemarks: row.DeliveryNote || "",
        PaymentRemarks: row.PaymentRemark || "",
      }));
      setCustomers(formatted);
      setFilteredCustomers(formatted); // 🔹 Initially show all
    } catch (error) {
      console.error(error);
      alert("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    const interval = setInterval(() => {
      fetchDashboardData();
    }, 120000);

    return () => clearInterval(interval);
  }, [setIsLoggedIn]);

  // 🔹 Search filter logic
  useEffect(() => {
    if (!searchText) {
      setFilteredCustomers(customers);
    } else {
      const lowerSearch = searchText.toLowerCase();
      const filtered = customers.filter(
        (c) =>
          c.name?.toLowerCase().includes(lowerSearch) ||
          c.mobile?.toLowerCase().includes(lowerSearch) ||
          String(c.OrdNo).includes(lowerSearch) ||
          c.circle?.toLowerCase().includes(lowerSearch)
      );
      setFilteredCustomers(filtered);
    }
  }, [searchText, customers]);

  const handleViewCustomer = (customer) => setSelectedCustomer(customer);

  const handleViewItems = async (order) => {
    try {
      if (!order?.oid) return;
      const response = await api.get("GetOnlineItemList", { oid: order.oid });
      const orderItems = response?.GetOnlineItemListjs?.Table || [];
      setSelectedOrders({ ...order, orders: orderItems });
    } catch (error) {
      console.error(error);
      alert("Failed to fetch items for this order.");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("isLoggedIn");
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("lastVisited");
    setIsLoggedIn(false);
  };

  const exportDashboardToExcel = () => {
    if (!customers.length) return;

    const exportData = customers.map((row, index) => ({
      Slno: index + 1,
      Name: row.name,
      Phone: row.mobile,
      Address: row.address,
      Circle: row.circle,
      OrdNo: row.OrdNo,
      Item: row.Item || "",
      Count: row.ItemCnt || 0,
      ODate: row.ODate,
      DvlDate: row.DvlDate,
      Mode: row.Paymode,
      Amount: row.Amount,
      Status: row.verified ? "Verified" : "New",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Dashboard");

    XLSX.writeFile(
      wb,
      `Customer_Dashboard_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-4 border-t-4 border-orange-500 rounded-full animate-spin"></div>
          <p className="text-lg text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onLogout={handleLogout} />
      <main className="p-6 md:ml-64">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
          <h2 className="text-2xl font-semibold text-gray-800">🧾 Customer Dashboard</h2>

        

            <div className="text-lg text-gray-700 flex items-center gap-3">
              👋 Welcome,<span className="font-semibold text-orange-600">{username}</span>
              <button
                onClick={exportDashboardToExcel}
                className="px-2 py-1 font-semibold inline-flex w-auto self-start text-white bg-blue-500 hover:bg-blue-700 rounded-full shadow transition"
              >
                📄 Export Excel
              </button>
            </div>           
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-3 w-full">
  <input
    type="text"
    placeholder="🔍 Search by Name, Phone, Order No, Circle"
    value={searchText}
    onChange={(e) => setSearchText(e.target.value)}
    className="ml-auto mr-4 px-3 py-2 w-full md:w-72 rounded-lg border border-gray-300 
               focus:outline-none focus:ring-2 focus:ring-orange-400"
  />
</div>


        <div className="relative -mx-6 sm:-mx-0">
          <div className="overflow-x-auto">
            <table className="min-w-[1200px] w-full">
              <CustomerList
                customers={filteredCustomers} // 🔹 Use filtered customers
                onView={handleViewCustomer}
                onItemClick={handleViewItems}
              />
            </table>
          </div>
        </div>

        {selectedCustomer && (
          <CustomerModal
            customer={selectedCustomer}
            onClose={() => setSelectedCustomer(null)}
          />
        )}

        {selectedOrders && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="w-full max-w-3xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200 animate-fadeIn">
              <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-400 text-white">
                <h2 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
                  🍽 <span>Order Details – <span className="font-bold">{selectedOrders.name}</span></span>
                </h2>
                <button
                  onClick={() => setSelectedOrders(null)}
                  className="px-3 py-1.5 text-sm font-medium bg-white text-orange-600 rounded-lg hover:bg-orange-100 hover:scale-105 transition"
                >
                  ✖
                </button>
              </div>

              <div className="p-6 overflow-x-auto bg-gradient-to-b from-white to-orange-50">
                <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-200 text-gray-800 text-sm font-semibold">
                    <tr>
                      <th className="p-3 text-left border border-gray-200">Food Type</th>
                      <th className="p-3 text-left border border-gray-200">Item</th>
                      <th className="p-3 text-center border border-gray-200">Qty</th>
                      <th className="p-3 text-right border border-gray-200">Amount </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrders.orders.length > 0 ? (
                      selectedOrders.orders.map((item, idx) => (
                        <tr
                          key={idx}
                          className={`transition hover:bg-orange-50 ${idx % 2 === 0 ? "bg-white" : "bg-orange-50/30"}`}
                        >
                          <td className="p-3 border border-gray-200">{item.FoodType || "-"}</td>
                          <td className="p-3 border border-gray-200">{item.item_desc || "-"}</td>
                          <td className="p-3 text-center border border-gray-200 font-medium">{item.Qty || 0}</td>
                          <td className="p-3 text-right border border-gray-200 font-semibold text-gray-800">
                            ₹{item.Amount?.toFixed?.(2) || "0.00"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="p-4 text-center text-gray-500">
                          No items found for this order.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
